/** Classification of a parsed host against Supabase's known endpoint shapes.
 *  `pooler` = Supavisor session/transaction pooler; `direct` = the IPv6-only
 *  direct database host; `unknown` = anything else (custom domain, self-hosted,
 *  or a typo) — surfaced as a soft warning, never a hard rejection. */
export type SupabaseHostKind = "pooler" | "direct" | "unknown";

export interface ParsedSupabaseConnection {
  host: string;
  port: number;
  database: string;
  username: string;
  /** Absent when the string carried no password or only a placeholder token. */
  password?: string;
  hostKind: SupabaseHostKind;
}

// Supabase dashboard strings ship the password as a bracketed placeholder the
// user is meant to replace. Treat these as "no password provided".
const PASSWORD_PLACEHOLDERS = new Set([
  "[your-password]",
  "[password]",
  "[yourpassword]",
]);

const classifyHost = (host: string): SupabaseHostKind => {
  const h = host.toLowerCase();
  if (h.endsWith(".pooler.supabase.com")) return "pooler";
  if (h.startsWith("db.") && h.endsWith(".supabase.co")) return "direct";
  return "unknown";
};

/** Parse a Postgres connection string (`postgresql://` or `postgres://`) into
 *  the fields MyCorr stores. Returns `null` when the input isn't a usable
 *  Postgres URL — callers fall back to manual entry. Components are
 *  URL-decoded; the username keeps its dot (`postgres.<project-ref>`). Port
 *  defaults to 5432 when absent. Does NOT reject non-Supabase hosts — the
 *  `hostKind` lets the UI warn without blocking. */
export const parseSupabaseConnectionString = (
  raw: string,
): ParsedSupabaseConnection | null => {
  const trimmed = raw.trim();
  if (!/^postgres(ql)?:\/\//i.test(trimmed)) return null;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  const host = url.hostname;
  if (!host) return null;

  const username = decodeURIComponent(url.username);
  if (!username) return null;

  const rawPassword = decodeURIComponent(url.password);
  const password =
    rawPassword && !PASSWORD_PLACEHOLDERS.has(rawPassword.toLowerCase())
      ? rawPassword
      : undefined;

  const port = url.port ? Number(url.port) : 5432;
  if (!Number.isInteger(port) || port <= 0 || port > 65535) return null;

  // Strip the leading slash; default to `postgres` when the path is empty.
  const database =
    decodeURIComponent(url.pathname.replace(/^\//, "")) || "postgres";

  return {
    host,
    port,
    database,
    username,
    password,
    hostKind: classifyHost(host),
  };
};
